import React, { useState } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";

import Sidebar from "./components/Sidebar";
import Topbar from "./components/Topbar";
import ToastHost from "./components/ToastHost";
import GlobalSearchableSelect from "./components/GlobalSearchableSelect";
import { isAuthenticated } from "./utils/auth";
import { useGlobalAutoUppercase } from "./utils/autoUppercase";
import { useNumberInputBehavior } from "./utils/numberInputBehavior";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";

import BankMaster from "./pages/master/BankMaster";
import BuyerBankMaster from "./pages/master/BuyerBankMaster";
import BuyerMaster from "./pages/master/BuyerMaster";
import CompanyMaster from "./pages/master/CompanyMaster";
import ContactGroupMaster from "./pages/master/ContactGroupMaster";
import DocumentMaster from "./pages/master/DocumentMaster";
import ForexRate from "./pages/master/ForexRate";
import ItemISRIMaster from "./pages/master/ItemISRIMaster";
import ItemMaster from "./pages/master/ItemMaster";
import HSNMaster from "./pages/master/HSNMaster";
import PortMaster from "./pages/master/PortMaster";
import ShipperMaster from "./pages/master/ShipperMaster";
import PackingMaster from "./pages/master/PackingMaster";
import UserMaster from "./pages/master/UserMaster";
import UserTypeMaster from "./pages/master/UserTypeMaster";
import UserRights from "./pages/master/UserRights";
import CreditDebit from "./pages/master/CreditDebit";
import Commission from "./pages/master/Commission";
import GeographyMaster from "./pages/master/GeographyMaster";

import NewDeal from "./pages/transaction/NewDeal";
import NewDealGrid from "./pages/transaction/NewDealGrid";
import NewInvoiceGrid from "./pages/transaction/InvoiceList";
import NewInvoice from "./pages/transaction/NewInvoice";
import ContainerRelease from "./pages/transaction/ContainerRelease";
import ShipmentSchedule from "./pages/transaction/ShipmentSchedule";
import PurchaseContract from "./pages/transaction/PurchaseContract";
import SalesContract from "./pages/transaction/SalesContract";
import AdvancePayment from "./pages/transaction/AdvancePayment";
import Attachment from "./pages/transaction/Attachment";
import Claim from "./pages/transaction/Claim";

// import ViewBankList from "./pages/report/ViewBankList";
// import ViewBuyerBankList from "./pages/report/ViewBuyerBankList";
// import ViewBuyerList from "./pages/report/ViewBuyerList";
// import ViewContactDetail from "./pages/report/ViewContactDetail";
// import ViewContactGroupList from "./pages/report/ViewContactGroupList";
// import ViewDeal from "./pages/report/ViewDeal";
// import ViewDocumentList from "./pages/report/ViewDocumentList";
// import ViewItemISRIList from "./pages/report/ViewItemISRIList";
// import ViewItemList from "./pages/report/ViewItemList";
// import ViewOpeningBalanceList from "./pages/report/ViewOpeningBalanceList";
// import ViewPortList from "./pages/report/ViewPortList";
// import ViewShipperList from "./pages/report/ViewShipperList";
// import ViewUserList from "./pages/report/ViewUserList";
import DealStatusReport from "./pages/report/DealStatusReport";
import ShipmentScheduleReport from "./pages/report/ShipmentScheduleReport";
import ShipmentPendingReport from "./pages/report/ShipmentPendingReport";
import SalesContractPendingReport from "./pages/report/SalesContractPendingReport";
import CommonFixationReport from "./pages/report/CommonFixationReport";
import ClaimReport from "./pages/report/ClaimReport";
import CopyDocPendingReport from "./pages/report/CopyDocPendingReport";
import OverdueAdvanceReport from "./pages/report/OverdueAdvanceReport";
import OverduePaymentStatusReport from "./pages/report/OverduePaymentStatusReport";
import ManualReportQueue from "./pages/report/ManualReportQueue";
import SupplierStatement from "./pages/report/SupplierStatement";
import CommissionReport from "./pages/report/CommissionReport";
import ViewOpeningBalanceList from "./pages/report/ViewOpeningBalanceList";
import SalesReport from "./pages/report/SalesReport";
import WeightSlipReport from "./pages/report/WeightSlipReport";
import AdditionalReports from "./pages/report/AdditionalReports";
import CrystalReports from "./pages/report/CrystalReports";

import YearSetting from "./pages/tools/YearSetting";
import DatabaseDetail from "./pages/tools/DatabaseDetail";
import GridSettings from "./pages/tools/GridSettings";

const MainLayout = ({ children, pageClassName = "" }) => {
  // Scoped to the authenticated app shell (not the login page, whose
  // username field could be case-sensitive depending on DB collation).
  useGlobalAutoUppercase();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => localStorage.getItem("erpSidebarCollapsed") === "true"
  );

  const toggleSidebar = () => {
    setSidebarCollapsed((current) => {
      const next = !current;
      localStorage.setItem("erpSidebarCollapsed", String(next));
      return next;
    });
  };

  if (!isAuthenticated()) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className={`erp-shell${sidebarCollapsed ? " erp-sidebar-collapsed" : ""}`}>
      <aside className="erp-sidebar" aria-hidden={sidebarCollapsed}>
        {!sidebarCollapsed && <Sidebar />}
      </aside>

      <div className="erp-workspace">
        <Topbar sidebarCollapsed={sidebarCollapsed} onToggleSidebar={toggleSidebar} />
        <main className="erp-main-content">
          <div className={`erp-page ${pageClassName}`.trim()}>{children}</div>
        </main>
      </div>
    </div>
  );
};

const MasterLayout = ({ children }) => (
  <MainLayout pageClassName="master-page-scope">{children}</MainLayout>
);

function App() {
  useNumberInputBehavior();
  return (
    <Router>
      <ToastHost />
      <GlobalSearchableSelect />
      <Routes>
        <Route path="/" element={<Navigate to="/login" replace />} />
        <Route path="/login" element={<Login />} />
        <Route path="/dashboard" element={<MainLayout><Dashboard /></MainLayout>} />

        <Route path="/master/bankmaster" element={<MasterLayout><BankMaster /></MasterLayout>} />
        <Route path="/master/buyerbankmaster" element={<MasterLayout><BuyerBankMaster /></MasterLayout>} />
        <Route path="/master/buyermaster" element={<MasterLayout><BuyerMaster /></MasterLayout>} />
        <Route path="/master/companymaster" element={<Navigate to="/tools/company-setting" replace />} />
        <Route path="/master/contactgroupmaster" element={<MasterLayout><ContactGroupMaster /></MasterLayout>} />
        <Route path="/master/documentmaster" element={<MasterLayout><DocumentMaster /></MasterLayout>} />
        <Route path="/master/forexrate" element={<MasterLayout><ForexRate /></MasterLayout>} />
        <Route path="/master/itemisrimaster" element={<MasterLayout><ItemISRIMaster /></MasterLayout>} />
        <Route path="/master/itemmaster" element={<MasterLayout><ItemMaster /></MasterLayout>} />
        <Route path="/master/hsnmaster" element={<MasterLayout><HSNMaster /></MasterLayout>} />
        <Route path="/master/portmaster" element={<MasterLayout><PortMaster /></MasterLayout>} />
        <Route path="/master/shippermaster" element={<MasterLayout><ShipperMaster /></MasterLayout>} />
        <Route path="/master/packingmaster" element={<MasterLayout><PackingMaster /></MasterLayout>} />
        <Route path="/master/usermaster" element={<MasterLayout><UserMaster /></MasterLayout>} />
        <Route path="/master/usertypemaster" element={<MasterLayout><UserTypeMaster /></MasterLayout>} />
        <Route path="/master/userrights" element={<MasterLayout><UserRights /></MasterLayout>} />
        <Route path="/master/credit-debit" element={<MasterLayout><CreditDebit /></MasterLayout>} />
        <Route path="/master/commission" element={<MasterLayout><Commission /></MasterLayout>} />
        <Route path="/master/countrymaster" element={<MasterLayout><GeographyMaster type="country" /></MasterLayout>} />
        <Route path="/master/statemaster" element={<MasterLayout><GeographyMaster type="state" /></MasterLayout>} />

        <Route path="/transaction/new-deal/add" element={<MainLayout><NewDeal /></MainLayout>} />
        <Route path="/transaction/new-deal/edit/:id" element={<MainLayout><NewDeal /></MainLayout>} />
        <Route path="/transaction/new-deal/list" element={<MainLayout><NewDealGrid /></MainLayout>} />
        <Route path="/transaction/new-invoice/list" element={<MainLayout><NewInvoiceGrid /></MainLayout>} />
        <Route path="/transaction/new-invoice/add" element={<MainLayout><NewInvoice /></MainLayout>} />
        <Route path="/transaction/new-invoice/edit/:id" element={<MainLayout><NewInvoice /></MainLayout>} />
        <Route path="/transaction/container-release/:dealId" element={<MainLayout><ContainerRelease /></MainLayout>} />
        <Route path="/transaction/claim/:dealId" element={<MainLayout><Claim /></MainLayout>} />
        <Route path="/transaction/shipment-schedule/:dealId" element={<MainLayout><ShipmentSchedule /></MainLayout>} />
        <Route path="/transaction/purchase-contract/:dealId" element={<MainLayout><PurchaseContract /></MainLayout>} />
        <Route path="/transaction/sales-contract/:dealId" element={<MainLayout><SalesContract /></MainLayout>} />
        <Route path="/transaction/advance-payment/:dealId" element={<MainLayout><AdvancePayment /></MainLayout>} />
        <Route path="/transaction/attachment/:dealId" element={<MainLayout><Attachment /></MainLayout>} />

       
        <Route path="/report/deal-status" element={<MainLayout><DealStatusReport /></MainLayout>} />
        <Route path="/report/shipment-schedule" element={<MainLayout><ShipmentScheduleReport /></MainLayout>} />
        <Route path="/report/shipment-pending" element={<MainLayout><ShipmentPendingReport /></MainLayout>} />
        <Route path="/report/sales-contract-pending" element={<MainLayout><SalesContractPendingReport /></MainLayout>} />
        <Route path="/report/common-fixation" element={<MainLayout><CommonFixationReport /></MainLayout>} />
        <Route path="/report/claim" element={<MainLayout><ClaimReport /></MainLayout>} />
        <Route path="/report/copy-doc-pending" element={<MainLayout><CopyDocPendingReport /></MainLayout>} />
        <Route path="/report/overdue-advance" element={<MainLayout><OverdueAdvanceReport /></MainLayout>} />
        <Route path="/report/overdue-payment" element={<MainLayout><OverduePaymentStatusReport /></MainLayout>} />
        <Route path="/report/manual-queue" element={<MainLayout><ManualReportQueue /></MainLayout>} />
        <Route path="/report/supplier-statement" element={<MainLayout><SupplierStatement /></MainLayout>} />
        <Route path="/report/commission" element={<MainLayout><CommissionReport /></MainLayout>} />
        <Route path="/report/opening-balance" element={<MainLayout><ViewOpeningBalanceList /></MainLayout>} />
        <Route path="/report/sales-report" element={<MainLayout><SalesReport /></MainLayout>} />
        <Route path="/report/weight-slip" element={<MainLayout><WeightSlipReport /></MainLayout>} />
        <Route path="/report/pending-status" element={<MainLayout><AdditionalReports key="pending" pending /></MainLayout>} />
        <Route path="/report/accounts/:reportKey" element={<MainLayout><AdditionalReports /></MainLayout>} />
        <Route path="/report/additional" element={<MainLayout><AdditionalReports /></MainLayout>} />
        <Route path="/report/crystal-reports" element={<Navigate to="/report/crystal-reports/buyer" replace />} />
        <Route path="/report/crystal-reports/:reportType" element={<MainLayout><CrystalReports /></MainLayout>} />

        <Route path="/tools/company-setting" element={<MasterLayout><CompanyMaster /></MasterLayout>} />
        <Route path="/tools/year-setting" element={<MainLayout><YearSetting /></MainLayout>} />
        <Route path="/tools/grid-settings" element={<MainLayout><GridSettings /></MainLayout>} />
        <Route path="/tools/database-detail" element={<MainLayout><DatabaseDetail /></MainLayout>} />
      </Routes>
    </Router>
  );
}

export default App;
